import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useScanPayPointQuery } from "Api/Queries/PayPoints/useScanPayPointQuery";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { StandardPaymentForm } from "Components/Customer/Payment/PaymentForms/StandardPaymentForm";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ErrorWithRetry } from "Components/Shared/Errors/ErrorWithRetry";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { pickMainBankAccount } from "Utils/AccountUtils";
import { appUrl } from "Utils/UrlUtils";
import { useParams } from "react-router-dom";

const PageResources = Resources.Customer.Pay;

export const FixedCollectorPaymentPage = () => {
  const { t } = useResource();
  const { id: paypointPublicID } = useParams<{ id: string }>();

  const {
    data: scanData,
    isFetching: isScanning,
    isError: hasScanFailed,
    refetch: retryScan,
  } = useScanPayPointQuery(paypointPublicID);

  const transactionPublicID = scanData?.transactionPublicID;

  const {
    data: bankAccountsData,
    isLoading: isLoadingBankAccounts,
    isError: hasBankAccountsLoadFailed,
    refetch: retryBankAccountsLoad,
  } = useBankingAccounts();

  const {
    data: transactionDetails,
    isLoading: isLoadingTransactionDetails,
    isError: hasDetailLoadFailed,
    refetch: retryDetailLoad,
  } = useTransactionsDetail({ publicID: transactionPublicID });

  const isLoading =
    isScanning || isLoadingBankAccounts || isLoadingTransactionDetails;

  if (!paypointPublicID) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Error.InvalidPaypointId)}</ErrorText>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (hasScanFailed) {
    return (
      <Layout>
        <ErrorWithRetry
          message={t(Resources.Hooks.Paypoint.ScanError)}
          onRetry={() => retryScan()}
        />
      </Layout>
    );
  }

  if (hasDetailLoadFailed || !transactionDetails) {
    return (
      <Layout>
        <ErrorWithRetry
          message={t(Resources.TransactionErrors.TransactionNotFound)}
          onRetry={() => retryDetailLoad()}
        />
      </Layout>
    );
  }

  if (hasBankAccountsLoadFailed || !bankAccountsData) {
    return (
      <Layout>
        <ErrorWithRetry
          message={t(Resources.Errors.DataLoadingError)}
          onRetry={() => retryBankAccountsLoad()}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <StandardPaymentForm
        transactionDetails={transactionDetails}
        bankAccounts={bankAccountsData.items}
        defaultValues={{
          publicID: transactionPublicID,
          bankAccountPublicID: pickMainBankAccount(
            bankAccountsData.items,
            transactionDetails.currencyCode,
          )?.publicID,
        }}
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(PageResources.Title)}
      backNavigation={{ mode: "staticUrl", url: appUrl("dashboard") }}
      isBottomMenuHidden
    >
      <StyledPageWrapper>{children}</StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
